/** Shared loot chip qty/quality — no calendar/benefits imports (safe for ScItemChip). */

export const LOOT_QTY_MIN = 1;
export const LOOT_QTY_MAX = 999999;
export const LOOT_SCU_MIN = 0.0001;
export const LOOT_SCU_DECIMALS = 4;
export const LOOT_QUALITY_MIN = 1;
export const LOOT_QUALITY_MAX = 1000;
export const LOOT_QUALITY_DEFAULT = 500;

export function isScuUnitKind(unitKind) {
  return String(unitKind || "").toUpperCase() === "SCU";
}

export function roundScuQuantity(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return LOOT_SCU_MIN;
  const factor = 10 ** LOOT_SCU_DECIMALS;
  return Math.round(n * factor) / factor;
}

/** Strip display-only Q prefix (Q500 → 500). Never persist the letter Q. */
export function parseItemQualityInput(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.trunc(raw);
  const s = String(raw).trim();
  if (!s) return null;
  const m = s.match(/^Q?\s*(\d{1,4})\s*Q?$/i);
  if (m) return Math.trunc(Number(m[1]));
  const n = Math.trunc(Number(s));
  return Number.isFinite(n) ? n : null;
}

export function clampLootQuality(value) {
  const parsed = parseItemQualityInput(value);
  if (parsed == null) return LOOT_QUALITY_DEFAULT;
  return Math.max(LOOT_QUALITY_MIN, Math.min(LOOT_QUALITY_MAX, parsed));
}

export function clampLootQuantity(value, unitKind = null) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return isScuUnitKind(unitKind) ? LOOT_SCU_MIN : LOOT_QTY_MIN;
  }
  if (isScuUnitKind(unitKind)) {
    return Math.max(LOOT_SCU_MIN, Math.min(LOOT_QTY_MAX, roundScuQuantity(n)));
  }
  return Math.max(LOOT_QTY_MIN, Math.min(LOOT_QTY_MAX, Math.trunc(n)));
}

export function formatLootQuantityDisplay(value, unitKind = null) {
  const qty = clampLootQuantity(value, unitKind);
  if (isScuUnitKind(unitKind)) {
    const fixed = roundScuQuantity(qty).toFixed(LOOT_SCU_DECIMALS);
    return fixed.replace(/\.?0+$/, "") || "0";
  }
  return String(qty);
}

export function sanitizeLootQuantityDraft(raw, unitKind = null) {
  const text = String(raw ?? "");
  if (!isScuUnitKind(unitKind)) {
    const digits = text.replace(/\D/g, "").slice(0, 6);
    return digits;
  }
  let normalized = text.replace(/,/g, ".").replace(/[^\d.]/g, "");
  const dotIndex = normalized.indexOf(".");
  if (dotIndex >= 0) {
    const whole = normalized.slice(0, dotIndex).replace(/\./g, "");
    const fraction = normalized.slice(dotIndex + 1).replace(/\./g, "").slice(0, LOOT_SCU_DECIMALS);
    normalized = fraction.length > 0 ? `${whole}.${fraction}` : `${whole}.`;
  }
  return normalized.slice(0, 12);
}

export function minLootQuantity(unitKind = null) {
  return isScuUnitKind(unitKind) ? LOOT_SCU_MIN : LOOT_QTY_MIN;
}

export function lootQuantityStep(unitKind = null) {
  return isScuUnitKind(unitKind) ? 0.1 : 1;
}

export function unitKindFromLootEntry(loot) {
  if (!loot || typeof loot !== "object") return null;
  if (loot.meta?.unitKindDefault != null) return String(loot.meta.unitKindDefault);
  if (loot.row?.unitKindDefault != null) return String(loot.row.unitKindDefault);
  return null;
}

export function quantitiesNearlyEqual(a, b) {
  return Math.abs(Number(a) - Number(b)) < 10 ** -(LOOT_SCU_DECIMALS + 1);
}

export function isFullLootQuantityMove(sourceQty, moveQty) {
  const src = Number(sourceQty) || 0;
  const moved = Number(moveQty) || 0;
  if (!Number.isFinite(src) || !Number.isFinite(moved)) return false;
  return quantitiesNearlyEqual(src, moved) || moved >= src - 10 ** -(LOOT_SCU_DECIMALS + 1);
}
